import * as React from 'react';
import {
  buildDateFromParts,
  getDaysInMonth,
  getYearRange,
  resolveInitialDate,
  stripTime,
} from './datePicker.utils';

const DATE_PICKER_OPTION_HEIGHT = 32;
const DATE_PICKER_VISIBLE_OPTIONS = 7;
const DATE_PICKER_PICKER_HEIGHT = DATE_PICKER_OPTION_HEIGHT * DATE_PICKER_VISIBLE_OPTIONS;
const DATE_PICKER_WHEEL_BUFFER_OPTIONS = 3;
const DATE_PICKER_SCROLL_SETTLE_DELAY = 140;

type DatePickerColumn = 'month' | 'day' | 'year';

type UseDatePickerWheelParams = {
  isOpen: boolean;
  value?: Date | null;
  defaultValue?: Date | null;
  minDate?: Date;
  maxDate?: Date;
};

function getAllowedMonths(year: number, minDate?: Date, maxDate?: Date) {
  const minMonth = minDate && minDate.getFullYear() === year ? minDate.getMonth() : 0;
  const maxMonth = maxDate && maxDate.getFullYear() === year ? maxDate.getMonth() : 11;

  return Array.from({length: maxMonth - minMonth + 1}, (_, index) => minMonth + index);
}

function getAllowedDays(year: number, month: number, minDate?: Date, maxDate?: Date) {
  const minDay =
    minDate && minDate.getFullYear() === year && minDate.getMonth() === month
      ? minDate.getDate()
      : 1;
  const maxDay =
    maxDate && maxDate.getFullYear() === year && maxDate.getMonth() === month
      ? maxDate.getDate()
      : getDaysInMonth(year, month);

  return Array.from({length: maxDay - minDay + 1}, (_, index) => minDay + index);
}

function resolveBoundedDateParts(
  year: number,
  month: number,
  day: number,
  minDate?: Date,
  maxDate?: Date,
) {
  const allowedMonths = getAllowedMonths(year, minDate, maxDate);
  const boundedMonth = Math.min(
    Math.max(month, allowedMonths[0] ?? month),
    allowedMonths[allowedMonths.length - 1] ?? month,
  );
  const allowedDays = getAllowedDays(year, boundedMonth, minDate, maxDate);
  const boundedDay = Math.min(
    Math.max(day, allowedDays[0] ?? day),
    allowedDays[allowedDays.length - 1] ?? day,
  );

  return buildDateFromParts(year, boundedMonth, boundedDay);
}

function getOptionScrollTop(index: number) {
  return index * DATE_PICKER_OPTION_HEIGHT;
}

function setListScrollTop(
  list: HTMLDivElement | null,
  top: number,
  behavior: ScrollBehavior,
) {
  if (!list) return;

  if (behavior === 'smooth') {
    list.scrollTo({top, behavior});
    return;
  }

  list.scrollTop = top;
}

function useDatePickerWheel({
  isOpen,
  value,
  defaultValue,
  minDate,
  maxDate,
}: UseDatePickerWheelParams) {
  const years = React.useMemo(() => getYearRange(minDate, maxDate), [maxDate, minDate]);
  const [draftDate, setDraftDate] = React.useState(() =>
    resolveInitialDate({value, defaultValue, minDate, maxDate}),
  );
  const draftYear = draftDate.getFullYear();
  const draftMonth = draftDate.getMonth();
  const [monthScrollTop, setMonthScrollTop] = React.useState(0);
  const [dayScrollTop, setDayScrollTop] = React.useState(0);
  const [yearScrollTop, setYearScrollTop] = React.useState(0);
  const monthListRef = React.useRef<HTMLDivElement>(null);
  const dayListRef = React.useRef<HTMLDivElement>(null);
  const yearListRef = React.useRef<HTMLDivElement>(null);
  const settleTimeoutsRef = React.useRef<Partial<Record<DatePickerColumn, number>>>({});
  const animationFramesRef = React.useRef<Partial<Record<DatePickerColumn, number>>>({});
  const columnRefs = React.useMemo(
    () => ({
      month: monthListRef,
      day: dayListRef,
      year: yearListRef,
    }),
    [],
  );

  const setColumnScrollTop = React.useCallback(
    (column: DatePickerColumn, nextScrollTop: number) => {
      if (column === 'month') {
        setMonthScrollTop(nextScrollTop);
        return;
      }

      if (column === 'day') {
        setDayScrollTop(nextScrollTop);
        return;
      }

      setYearScrollTop(nextScrollTop);
    },
    [],
  );

  const clearSettleTimeout = React.useCallback((column: DatePickerColumn) => {
    const timeoutId = settleTimeoutsRef.current[column];
    if (timeoutId === undefined) return;

    window.clearTimeout(timeoutId);
    delete settleTimeoutsRef.current[column];
  }, []);

  const clearAnimationFrame = React.useCallback((column: DatePickerColumn) => {
    const frameId = animationFramesRef.current[column];
    if (frameId === undefined) return;

    window.cancelAnimationFrame(frameId);
    delete animationFramesRef.current[column];
  }, []);

  React.useEffect(
    () => () => {
      (['month', 'day', 'year'] as const).forEach(column => {
        clearSettleTimeout(column);
        clearAnimationFrame(column);
      });
    },
    [clearAnimationFrame, clearSettleTimeout],
  );

  React.useEffect(() => {
    if (isOpen) return;

    setDraftDate(resolveInitialDate({value, defaultValue, minDate, maxDate}));
  }, [defaultValue, isOpen, maxDate, minDate, value]);

  const months = React.useMemo(
    () => getAllowedMonths(draftYear, minDate, maxDate),
    [draftYear, maxDate, minDate],
  );
  const days = React.useMemo(
    () => getAllowedDays(draftYear, draftMonth, minDate, maxDate),
    [draftMonth, draftYear, maxDate, minDate],
  );

  const monthIndex = months.findIndex(month => month === draftMonth);
  const dayIndex = days.findIndex(day => day === draftDate.getDate());
  const yearIndex = years.findIndex(year => year === draftYear);

  const syncScrollPositions = React.useCallback(
    (nextDate: Date, behavior: ScrollBehavior = 'auto') => {
      const nextMonths = getAllowedMonths(nextDate.getFullYear(), minDate, maxDate);
      const nextMonthIndex = nextMonths.findIndex(month => month === nextDate.getMonth());
      const nextDays = getAllowedDays(
        nextDate.getFullYear(),
        nextDate.getMonth(),
        minDate,
        maxDate,
      );
      const nextDayIndex = nextDays.findIndex(day => day === nextDate.getDate());
      const nextMonthTop = getOptionScrollTop(Math.max(nextMonthIndex, 0));
      const nextDayTop = getOptionScrollTop(Math.max(nextDayIndex, 0));
      const nextYearIndex = years.findIndex(year => year === nextDate.getFullYear());
      const nextYearTop = getOptionScrollTop(Math.max(nextYearIndex, 0));

      setMonthScrollTop(nextMonthTop);
      setDayScrollTop(nextDayTop);
      setYearScrollTop(nextYearTop);

      setListScrollTop(monthListRef.current, nextMonthTop, behavior);
      setListScrollTop(dayListRef.current, nextDayTop, behavior);
      setListScrollTop(yearListRef.current, nextYearTop, behavior);
    },
    [maxDate, minDate, years],
  );

  React.useLayoutEffect(() => {
    if (!isOpen) return;

    const nextDate = resolveInitialDate({value, defaultValue, minDate, maxDate});
    setDraftDate(nextDate);

    const frameId = window.requestAnimationFrame(() => {
      syncScrollPositions(nextDate);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [defaultValue, isOpen, maxDate, minDate, syncScrollPositions, value]);

  const updateDraftDate = React.useCallback(
    (
      column: DatePickerColumn,
      targetIndex: number,
      behavior: ScrollBehavior = 'smooth',
    ) => {
      const currentDate = stripTime(draftDate);
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth();
      const currentDay = currentDate.getDate();

      let nextDate = currentDate;

      if (column === 'month') {
        const nextMonth = months[targetIndex];

        if (nextMonth === undefined) {
          return;
        }

        nextDate = resolveBoundedDateParts(
          currentYear,
          nextMonth,
          currentDay,
          minDate,
          maxDate,
        );
      }

      if (column === 'day') {
        const nextDay = days[targetIndex];

        if (nextDay === undefined) {
          return;
        }

        nextDate = buildDateFromParts(currentYear, currentMonth, nextDay);
      }

      if (column === 'year') {
        const nextYear = years[targetIndex];

        if (nextYear === undefined) {
          return;
        }

        nextDate = resolveBoundedDateParts(
          nextYear,
          currentMonth,
          currentDay,
          minDate,
          maxDate,
        );
      }

      setDraftDate(nextDate);
      syncScrollPositions(nextDate, behavior);
    },
    [days, draftDate, maxDate, minDate, months, syncScrollPositions, years],
  );

  const settleColumnScroll = React.useCallback(
    (column: DatePickerColumn) => {
      const list = columnRefs[column].current;
      if (!list) return;

      const maxIndex =
        column === 'month'
          ? months.length - 1
          : column === 'day'
            ? days.length - 1
            : years.length - 1;
      const nextIndex = Math.max(
        0,
        Math.min(maxIndex, Math.round(list.scrollTop / DATE_PICKER_OPTION_HEIGHT)),
      );

      updateDraftDate(column, nextIndex, 'auto');
    },
    [columnRefs, days.length, months.length, updateDraftDate, years.length],
  );

  const scheduleScrollSettle = React.useCallback(
    (column: DatePickerColumn) => {
      clearSettleTimeout(column);
      settleTimeoutsRef.current[column] = window.setTimeout(() => {
        settleColumnScroll(column);
      }, DATE_PICKER_SCROLL_SETTLE_DELAY);
    },
    [clearSettleTimeout, settleColumnScroll],
  );

  const handleColumnScroll = React.useCallback(
    (column: DatePickerColumn) => {
      const list = columnRefs[column].current;
      if (!list) return;

      const nextScrollTop = list.scrollTop;
      clearAnimationFrame(column);

      animationFramesRef.current[column] = window.requestAnimationFrame(() => {
        setColumnScrollTop(column, nextScrollTop);
        delete animationFramesRef.current[column];
      });

      scheduleScrollSettle(column);
    },
    [clearAnimationFrame, columnRefs, scheduleScrollSettle, setColumnScrollTop],
  );

  const handleOptionSelect = React.useCallback(
    (column: DatePickerColumn, targetIndex: number) => {
      updateDraftDate(column, targetIndex, 'smooth');
    },
    [updateDraftDate],
  );

  const focusAdjacentColumn = React.useCallback(
    (column: DatePickerColumn, direction: -1 | 1) => {
      const order: DatePickerColumn[] = ['month', 'day', 'year'];
      const currentIndex = order.indexOf(column);
      const targetColumn = order[currentIndex + direction];

      if (!targetColumn) return;

      columnRefs[targetColumn].current?.focus();
    },
    [columnRefs],
  );

  const handleColumnKeyDown = React.useCallback(
    (column: DatePickerColumn, event: React.KeyboardEvent<HTMLDivElement>) => {
      const currentIndex =
        column === 'month' ? monthIndex : column === 'day' ? dayIndex : yearIndex;
      const maxIndex =
        column === 'month'
          ? months.length - 1
          : column === 'day'
            ? days.length - 1
            : years.length - 1;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        updateDraftDate(column, Math.min(currentIndex + 1, maxIndex), 'smooth');
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        updateDraftDate(column, Math.max(currentIndex - 1, 0), 'smooth');
        return;
      }

      if (event.key === 'Home') {
        event.preventDefault();
        updateDraftDate(column, 0, 'smooth');
        return;
      }

      if (event.key === 'End') {
        event.preventDefault();
        updateDraftDate(column, maxIndex, 'smooth');
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        focusAdjacentColumn(column, -1);
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        focusAdjacentColumn(column, 1);
      }
    },
    [
      dayIndex,
      days.length,
      focusAdjacentColumn,
      monthIndex,
      months.length,
      updateDraftDate,
      yearIndex,
      years.length,
    ],
  );

  return {
    dayIndex,
    dayListRef,
    dayScrollTop,
    days,
    draftDate,
    handleColumnKeyDown,
    handleColumnScroll,
    handleOptionSelect,
    monthIndex,
    monthListRef,
    months,
    monthScrollTop,
    yearIndex,
    yearListRef,
    yearScrollTop,
    years,
  };
}

export {
  DATE_PICKER_OPTION_HEIGHT,
  DATE_PICKER_PICKER_HEIGHT,
  DATE_PICKER_VISIBLE_OPTIONS,
  DATE_PICKER_WHEEL_BUFFER_OPTIONS,
  useDatePickerWheel,
};
export type {DatePickerColumn};
